/**
 * Products a metal roof must never be offered.
 *
 * Jonathon, 2026-08-27: "exclude MasterFlow pipe boots as a selectable option
 * on all metal roof templates and metal roof estimates."
 *
 * The GAF Master Flow Pivot boot is a shingle product. It seats on a flat,
 * nailable deck under a course of shingles; on a standing seam or an exposed
 * fastener panel the ribs hold it off the metal and it leaks. What a metal
 * roof takes is a high temperature silicone boot, which is what the three
 * metal templates already carry. Nothing stopped somebody adding the wrong one
 * from a dropdown of four hundred products, and it appeared on the customer's
 * proposal as one of the four upgrades whatever roof was being sold.
 *
 * Matched on the name rather than on a column, for the same reason every other
 * reference value is free text (rule 8): the catalogue is what it is, the name
 * is what a person recognises, and adding a column would mean somebody has to
 * remember to tick it on the next boot the supplier lists.
 */

/** What a roof type has to be for this to bite. */
const METAL_ROOF_TYPES: readonly string[] = [
  "metal shingle",
  "standing seam metal",
  "exposed fastener metal panel",
  "ag panel 26 gauge residential",
  "r panel 26 gauge residential",
];

/** Letters and digits only, so "Master Flow™" and "MasterFlow" are one word. */
const squash = (value: string | null | undefined): string =>
  (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Whether a roof type is metal.
 *
 * Metal shingle — TimberSteel — is on the list. It is laid like a shingle roof
 * (which is why the scope of work page covers it, see `scopeOfWork.ts`), but a
 * pipe penetration through it is a metal penetration and takes a metal boot.
 *
 * A blank roof type is not metal. An estimate written from scratch records
 * nothing about what it is, and the safe reading of nothing is to leave the
 * catalogue alone rather than to hide products from somebody.
 */
export function isMetalRoofType(roofType: string | null | undefined): boolean {
  return METAL_ROOF_TYPES.includes((roofType ?? "").trim().toLowerCase());
}

/** Whether a product is one of the GAF Master Flow pipe boots. */
export function isMasterFlowPipeBoot(name: string | null | undefined): boolean {
  const squashed = squash(name);
  return squashed.includes("masterflow") && squashed.includes("boot");
}

/**
 * Whether a product may be chosen for a roof of this type.
 *
 * The only rule so far is the one above, and it is written as a general
 * question because the next one will be — a metal roof takes no shingle
 * starter and no shingle ridge cap either, and the day somebody says so this
 * is where it goes.
 */
export function productSuitsRoofType(
  product: { name?: string | null },
  roofType: string | null | undefined,
): boolean {
  if (!isMetalRoofType(roofType)) return true;
  return !isMasterFlowPipeBoot(product.name);
}

/** The catalogue, less anything this roof must not be sold. */
export function productsForRoofType<T extends { name?: string | null }>(
  products: readonly T[],
  roofType: string | null | undefined,
): T[] {
  if (!isMetalRoofType(roofType)) return [...products];
  return products.filter((product) => productSuitsRoofType(product, roofType));
}

/**
 * Whether an upgrade may be offered on a document quoting these roofs.
 *
 * **Every** roof has to be metal, not merely one of them. The upgrades sit in
 * one list under all the packages — there is no per-package tick — so a packet
 * offering a shingle option beside a standing seam one still offers the boots,
 * because they are real on the option most customers take. A packet that is
 * metal all the way down does not.
 *
 * A document quoting nothing quotes no metal, so nothing is hidden.
 */
export function upgradeSuitsRoofs(
  product: { name?: string | null },
  roofTypes: readonly (string | null | undefined)[],
): boolean {
  if (roofTypes.length === 0) return true;
  if (!roofTypes.every(isMetalRoofType)) return true;
  return !isMasterFlowPipeBoot(product.name);
}
